import { useCallback, useMemo, useState } from "react";

import { usersService } from "../services/users.service";
import type { CreateUserPayload, UpdateUserPayload, UserStatus } from "../types/create-user.types";
import type { InternalUserResponse, MutatedUserResponse } from "../types/users-api.types";
import { normalizeUserApiError, type NormalizedApiError } from "../utils/apiError";

export type MutationResult<T> = { ok: true; data: T } | { ok: false; error: NormalizedApiError };

/**
 * The fields the create drawer owns, for binding validation messages back to inputs.
 *
 * Exported because the drawer needs the same list to narrow `fieldErrors` — whose keys are `string` — down to
 * a `setError` field name. Sharing the tuple makes that narrowing a real type guard instead of an assertion,
 * and turns a field that drifts out of the form into a compile error at the `setError` call.
 */
export const CREATE_FIELDS = [
  "full_name",
  "email",
  "password",
  "organization_id",
  "country",
  "phone_number",
  "preferred_language",
] as const;

/**
 * The edit drawer's fields — must stay in step with what `UserDetailFields` actually renders, or an error
 * binds to an input that does not exist and vanishes. `organization_id` is absent because it is not editable
 * here, and `password` because the profile form does not render it: `setPassword` is a separate call with its
 * own field list, and `buildUserDiff` can never emit the key. Listing it here contradicted the rule above and
 * would silently swallow the first error of any future password path routed through `updateUser`.
 *
 * Exported for the same reason as `CREATE_FIELDS`: the drawer narrows with it, so "in step with the form" is
 * now checked by the compiler rather than by this comment.
 */
export const UPDATE_FIELDS = ["full_name", "email", "country", "phone_number", "preferred_language"] as const;

/**
 * Builds the PATCH body as a DIFF of what actually changed.
 *
 * This is the whole reason editing works: the screen this replaces resent the entire object, and `user_type:
 * "internal"` together with the root `organization_id` is a hard 400 — so every Coloca staff member was
 * uneditable. `UpdateUserPayload` cannot express either field, and this only emits keys whose value moved.
 *
 * An emptied optional string becomes `null`, never `""`. `@IsOptional()` skips `null` (so the column is
 * cleared) but does not skip an empty string, which then reaches the validator and 400s.
 */
export function buildUserDiff(
  current: InternalUserResponse,
  next: {
    full_name: string;
    email: string;
    country: string;
    phone_number: string;
    preferred_language: string;
  },
): UpdateUserPayload {
  const diff: UpdateUserPayload = {};

  const name = next.full_name.trim();
  if (name && name !== current.full_name) diff.full_name = name;

  const email = next.email.trim().toLowerCase();
  if (email && email !== current.email.toLowerCase()) diff.email = email;

  const nullable = (value: string, currentValue: string | null): string | null | undefined => {
    const trimmed = value.trim();
    const normalized = trimmed === "" ? null : trimmed;
    return normalized === (currentValue ?? null) ? undefined : normalized;
  };

  const country = nullable(next.country, current.country);
  if (country !== undefined) diff.country = country;

  const phone = nullable(next.phone_number, current.phone_number);
  if (phone !== undefined) diff.phone_number = phone;

  const language = nullable(next.preferred_language, current.preferred_language);
  if (language !== undefined) diff.preferred_language = language;

  return diff;
}

export interface UserMutations {
  isBusy: boolean;
  createUser: (payload: CreateUserPayload) => Promise<MutationResult<MutatedUserResponse>>;
  updateUser: (id: string, payload: UpdateUserPayload) => Promise<MutationResult<MutatedUserResponse>>;
  setPassword: (id: string, password: string) => Promise<MutationResult<MutatedUserResponse>>;
  setStatus: (id: string, status: UserStatus) => Promise<MutationResult<MutatedUserResponse>>;
  verifyEmail: (id: string) => Promise<MutationResult<MutatedUserResponse>>;
  deleteUser: (id: string) => Promise<MutationResult<void>>;
}

/**
 * Every mutation this screen can perform. None of them throws to the component: each resolves to a tagged
 * result carrying a normalised, field-bindable error, so a drawer can put a 409 on the email input instead of
 * firing a toast the operator has to translate into an action.
 */
export function useUserMutations(): UserMutations {
  const [isBusy, setIsBusy] = useState(false);

  const run = useCallback(
    async <T>(fn: () => Promise<T>, fields: readonly string[]): Promise<MutationResult<T>> => {
      setIsBusy(true);
      try {
        return { ok: true, data: await fn() };
      } catch (error: unknown) {
        return { ok: false, error: normalizeUserApiError(error, fields) };
      } finally {
        setIsBusy(false);
      }
    },
    [],
  );

  const createUser = useCallback(
    (payload: CreateUserPayload) => run(() => usersService.create(payload), CREATE_FIELDS),
    [run],
  );

  const updateUser = useCallback(
    (id: string, payload: UpdateUserPayload) => run(() => usersService.update(id, payload), UPDATE_FIELDS),
    [run],
  );

  const setPassword = useCallback(
    (id: string, password: string) => run(() => usersService.update(id, { password }), ["password"]),
    [run],
  );

  const setStatus = useCallback(
    (id: string, status: UserStatus) => run(() => usersService.update(id, { status }), []),
    [run],
  );

  const verifyEmail = useCallback((id: string) => run(() => usersService.verifyEmail(id), []), [run]);

  const deleteUser = useCallback((id: string) => run(() => usersService.remove(id), []), [run]);

  // Memoised for the same reason as `useUsersPageState`: the page threads these into the row-view memo, and
  // a new object per render would rebuild every row on every keystroke.
  return useMemo(
    () => ({ isBusy, createUser, updateUser, setPassword, setStatus, verifyEmail, deleteUser }),
    [isBusy, createUser, updateUser, setPassword, setStatus, verifyEmail, deleteUser],
  );
}
